import { memo, useCallback, useEffect, useRef } from 'react';

import { Entity, stringifyEntityRef } from '@backstage/catalog-model';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import {
  ScaffolderFieldError,
  ScaffolderFormPaper,
  ScaffolderInputHeader,
} from '../../../components';
import { useSupervisoryOrgOwner } from '../../../../hooks';
import { OWNER_INFO } from '../infoDoc';
import { useStyles } from '../styles';

export interface OwnerSectionProps {
  value: string;
  onChange: (value: string) => void;
  errors?: string[];
}

const OwnerSectionInner = ({
  value,
  onChange,
  errors = [],
}: OwnerSectionProps) => {
  const { classes } = useStyles();

  // Stable ref — keeps useCallback deps empty ([]) without stale closures.
  // Always read .current inside callbacks instead of closing over the prop directly.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const ownerError = errors.length > 0 && !value ? 'Owner is required' : '';
  const hasOwnerError = ownerError !== '';

  const ownerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (hasOwnerError) {
      ownerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [hasOwnerError]);

  const {
    ownerOptions,
    selectedOwner,
    loading: ownersLoading,
  } = useSupervisoryOrgOwner(value);

  const handleOwnerChange = useCallback((_: unknown, entity: Entity | null) => {
    onChangeRef.current(entity ? stringifyEntityRef(entity) : '');
  }, []);

  return (
    <Box>
      <ScaffolderFormPaper>
        <ScaffolderInputHeader
          title="Owner"
          description="Configure the ownership details for this Deployment as a Service Application."
          variant="h5"
          divider
        />
        <div ref={ownerRef} className={classes.inputContainer}>
          <ScaffolderInputHeader
            title="Application owner"
            description="Select the owner of this application."
            infoDoc={OWNER_INFO}
          />
          <Autocomplete
            size="small"
            value={selectedOwner}
            loading={ownersLoading}
            onChange={handleOwnerChange}
            options={ownerOptions}
            getOptionLabel={option =>
              (option.metadata.title || option.metadata.name) as string
            }
            isOptionEqualToValue={(option, selected) =>
              stringifyEntityRef(option) === stringifyEntityRef(selected)
            }
            renderInput={params => (
              <TextField
                {...params}
                label="Owner"
                variant="outlined"
                required
                size="small"
                error={hasOwnerError}
                helperText={
                  hasOwnerError ? (
                    <ScaffolderFieldError message={ownerError} />
                  ) : undefined
                }
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {ownersLoading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
            renderOption={({ key, ...optionProps }, option) => (
              <li key={key} {...optionProps}>
                <Box>
                  <Typography variant="body2" noWrap>
                    {option.metadata.title || option.metadata.name}
                  </Typography>
                  <Typography variant="caption" noWrap color="text.secondary">
                    {stringifyEntityRef(option)}
                  </Typography>
                </Box>
              </li>
            )}
            noOptionsText="No owners found"
            loadingText="Loading owners..."
          />
        </div>
      </ScaffolderFormPaper>
    </Box>
  );
};

export const OwnerSection = memo(OwnerSectionInner);
